import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@rabex-kit/rabex-ui';
import PrivateRoute from './components/PrivateRoute';
import * as routes from './pages/routes';

// ─────────────────────────────────────────────────────────────────────────────
// EAGER IMPORTS
// ─────────────────────────────────────────────────────────────────────────────
// These render on the auth/landing critical path. Splitting them costs more
// in Suspense flicker than it saves in bundle size, so they stay eager and
// land in the main entry alongside the router shell.
// ─────────────────────────────────────────────────────────────────────────────
import Login from './pages/Auth/Login';
import NotFound from './pages/NotFound';
import RouteFallback from './components/RouteFallback';
import PrebootDismisser from '@/components/PrebootDismisser';

// ─────────────────────────────────────────────────────────────────────────────
// LAZY IMPORTS
// ─────────────────────────────────────────────────────────────────────────────
// Each lazy() call produces its own dynamic import. Whether multiple lazy
// routes end up in the SAME chunk is controlled exclusively by the
// `manualChunks` function in vite.config.ts — NOT by any comment here.
//
// IMPORTANT: Rollup/Vite has no `@vite-chunk` magic comment. webpack's
// `webpackChunkName` does not apply. Grouping is configured in vite.config.ts.
// ─────────────────────────────────────────────────────────────────────────────

// Auth flows — grouped via vite.config.ts manualChunks rule for src/pages/Auth
const Forget = lazy(() => import('./pages/Auth/Forget'));
const ChangePassword = lazy(() => import('./pages/Auth/Forget/password'));
const Otp = lazy(() => import('./pages/Auth/Otp'));
const Register = lazy(() => import('./pages/Auth/Register'));
const GoogleRegister = lazy(() => import('./pages/Auth/GoogleRegister'));

// Trading / market — each standalone, heavy, frequently visited
const OTCVersion2 = lazy(() => import('./pages/OTC-version2'));
const P2p = lazy(() => import('./pages/P2p'));
const P2pMarketDetail = lazy(() => import('./pages/P2p/MarketDetail'));
const Markets = lazy(() => import('./pages/Markets'));
const AndroidP2PChart = lazy(
  () => import('./containers/Chart/AndroidP2PChart')
);

// KYC — grouped via vite.config.ts; user moves through these screens sequentially
const Kyc = lazy(() => import('./pages/Kyc'));

// Profile / account / security — grouped via vite.config.ts
const Profile = lazy(() => import('./pages/Profile'));
const ProfileMenuMobile = lazy(() => import('./pages/Profile/Menu'));
const Tiers = lazy(() => import('./pages/Tiers'));
const Referral = lazy(() => import('./pages/Referral'));
const Security = lazy(() => import('./pages/Security'));
const ActiveSessions = lazy(() => import('./pages/Security/Sessions'));
const Activities = lazy(() => import('./pages/Security/Activities'));

// Tickets — pulls in editor-vendor (draft-js) on Show/New
const Tickets = lazy(() => import('./pages/Tickets'));
const NewTicket = lazy(() => import('./pages/Tickets/New'));
const ShowTicket = lazy(() => import('./pages/Tickets/Show'));

// Wallet flows — grouped via vite.config.ts
// The frame the wallet pages share, so the header stays up between them
const AppShell = lazy(() => import('./components/AppShell'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Financial = lazy(() => import('./pages/Financial'));
const AddressBook = lazy(() => import('./pages/AddressBook'));
const FiatDeposit = lazy(() => import('./pages/FiatDeposit'));
const FiatWithdraw = lazy(() => import('./pages/FiatWithdraw'));
const CryptoDeposit = lazy(() => import('./pages/CryptoDeposit'));
const CryptoWithdraw = lazy(() => import('./pages/CryptoWithdraw'));

// History — grouped via vite.config.ts
const History = lazy(() => import('./pages/History'));
const OtcHistory = lazy(() => import('./pages/History/OtcHistory'));
const P2PHistory = lazy(() => import('./pages/History/P2PHistory'));
const SmallBalanceHistory = lazy(
  () => import('./pages/History/SmallBalanceHistory')
);

// Standalone pages — intentionally NOT grouped; each is heavy on its own
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PriceAlert = lazy(() => import('./pages/PriceAlert'));
const Notifications = lazy(() => import('./pages/Notifications'));
const RedirectToIpg = lazy(() => import('./pages/MobilePayment/RedirectToIpg'));
const GoftinoAndroid = lazy(() => import('./components/GoftinoAndroid'));
const RewardCenter = lazy(() => import('./pages/RewardCenter'));
const DigitalGold = lazy(() => import('./pages/DigitalGold'));
const RwaAssets = lazy(() => import('./pages/RwaAssets'));

// ─────────────────────────────────────────────────────────────────────────────
// HELPER COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const ReferralRedirectInline = () => {
  const { referralCode } = useParams();
  return <Navigate to={`/fa/auth/register/${referralCode}`} replace />;
};

/**
 * SmartRootRedirect
 *
 * For p.rabex.ir (or any root URL visit) on mobile, land on the public
 * dashboard instead of the blank /fa redirect.
 * Desktop keeps existing behavior.
 *
 * Component (not inline expression) because it uses hooks.
 */
const SmartRootRedirect = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  return <Navigate to={isMobile ? routes.DASHBOARD : '/fa'} replace />;
};

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE TREE
// ─────────────────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <PrebootDismisser>
        <Routes>
          <Route element={<AppShell />}>
            <Route element={<PrivateRoute />}>
              <Route path={routes.FIAT_DEPOSIT} element={<FiatDeposit />} />
              <Route
                path="/fa/my/wallet/account/main/deposit/fiat/TMN"
                element={
                  <Navigate
                    to="/fa/my/wallet/account/main/deposit/fiat/TMN/card-transfer"
                    replace
                  />
                }
              />

              <Route path={routes.FIAT_WITHDRAW} element={<FiatWithdraw />} />
              <Route path={routes.CRYPTO_DEPOSIT} element={<CryptoDeposit />} />
              <Route
                path={routes.CRYPTO_DEPOSIT_RAW}
                element={<CryptoDeposit />}
              />
              <Route
                path={routes.CRYPTO_WITHDRAW}
                element={<CryptoWithdraw />}
              />
              <Route
                path={routes.CRYPTO_WITHDRAW_RAW}
                element={<CryptoWithdraw />}
              />
              <Route path={routes.WALLET} element={<Wallet />} />
              <Route path={routes.WALLET_HISTORY} element={<History />} />
              <Route path={routes.OTC_HISTORY} element={<OtcHistory />} />
              <Route
                path={routes.SMALL_BALANCE_HISTORY}
                element={<SmallBalanceHistory />}
              />
              <Route path={routes.P2P_HISTORY} element={<P2PHistory />} />
              <Route path={routes.PORTFOLIO} element={<Portfolio />} />
              <Route path={routes.KYC} element={<Kyc />} />
              {/* <Route path={routes.KYC_BASIC} element={<KycContainer />} /> */}
              {/* <Route path={routes.KYC_BASIC_BASICINFO} element={<BasicInfo />} /> */}
              {/* <Route path={routes.KYC_ADVANCE} element={<KycContainer />} /> */}
              {/* <Route path={routes.KYC_ADVANCE_IDENTITIES} element={<Advance />} /> */}
              {/* <Route */}
              {/*   path={routes.KYC_UNDER_INVESTIGATIONS} */}
              {/*   element={<KycUnderInvestigation />} */}
              {/* /> */}
              {/* <Route */}
              {/*   path={routes.KYC_SUCCSESSFULL} */}
              {/*   element={<KycSuccessfull />} */}
              {/* /> */}
              {/* <Route path={routes.KYC_ERROR} element={<KycError />} /> */}
              {/* <Route */}
              {/*   path={routes.KYC_LEVEL_ONE_UNDERINVESTIGATION} */}
              {/*   element={<KycLevelOneUnderInvestigation />} */}
              {/* /> */}
              <Route path={routes.PROFILE} element={<Profile />} />
              <Route
                path={routes.PROFILE_LINKS}
                element={<ProfileMenuMobile />}
              />
              <Route path={routes.TIERS} element={<Tiers />} />
              <Route path={routes.REFERRAL} element={<Referral />} />
              <Route path={routes.ACTIVITIES} element={<Activities />} />
              <Route path={routes.TICKET} element={<Tickets />} />
              <Route path={routes.FINANCIAL} element={<Financial />} />
              <Route path={routes.ADDRESS_BOOK} element={<AddressBook />} />
              <Route path={routes.SECURITY} element={<Security />} />
              <Route
                path={routes.ACTIVESESSIONS}
                element={<ActiveSessions />}
              />
              <Route path={routes.NEW_TICKET} element={<NewTicket />} />
              <Route path={routes.PRICE_ALERT} element={<PriceAlert />} />
              <Route path={routes.NOTIFICATIONS} element={<Notifications />} />
            </Route>
            {/*
             * DASHBOARD — outside PrivateRoute (same pattern as OTC).
             * Auth logic is handled inside pages/Dashboard/index.tsx:
             *   desktop + unauth  → Navigate to LOGIN
             *   desktop + auth    → existing desktop dashboard
             *   mobile + unauth   → guest mobile dashboard
             *   mobile + auth     → auth mobile dashboard
             */}
            <Route path={routes.DASHBOARD} element={<Dashboard />} />

            <Route path={routes.OTC} element={<OTCVersion2 />} />
            {/* Outside PrivateRoute: guests browse the public campaigns, and
                the page asks them to log in where their own data would be. */}
            <Route path={routes.REWARD_CENTER} element={<RewardCenter />} />
            <Route path={routes.MARKETS} element={<Markets />} />
            <Route path={routes.DIGITAL_GOLD} element={<DigitalGold />} />
            <Route path={routes.RWA_ASSETS} element={<RwaAssets />} />
            <Route path={routes.SHOW_TICKET} element={<ShowTicket />} />
            <Route path={routes.P2P} element={<P2p />} />
          </Route>

          <Route
            path={routes.P2P_MARKET_DETAIL}
            element={<P2pMarketDetail />}
          />
          <Route path={routes.CHART} element={<AndroidP2PChart />} />

          <Route path={routes.AUTH} element={<Login />} />
          {/* More specific than AUTH, so /fa/auth/register/google lands here, not on the register tab. */}
          <Route path={routes.GOOGLE_REGISTER} element={<GoogleRegister />} />
          {/* Outside PrivateRoute: the code step runs on a pending token, which isn't a session yet.
              The page sends anyone who didn't come from login/register/forget back to login. */}
          <Route path={routes.OTP} element={<Otp />} />
          <Route path={routes.FORGET} element={<Forget />} />
          <Route path={routes.CHANGE_PASSWORD} element={<ChangePassword />} />

          <Route
            path="/goftino-app/:name?/:lastName?/:phone?/:uuid?/:email?"
            element={<GoftinoAndroid />}
          />
          <Route
            path="/auth/login"
            element={<Navigate to={routes.LOGIN} replace />}
          />
          <Route
            path="/auth/register"
            element={<Navigate to={routes.REGISTER} replace />}
          />
          <Route
            path="/auth/register/:referralCode"
            element={<ReferralRedirectInline />}
          />
          <Route
            path="/auth/login/forget"
            element={<Navigate to={routes.FORGET} replace />}
          />
          <Route
            path="/login"
            element={<Navigate to={routes.LOGIN} replace />}
          />
          <Route path={routes.REDIRECT_TO_IPG} element={<RedirectToIpg />} />

          {/* Root redirect: mobile → dashboard, desktop → /fa */}
          <Route path="/" element={<SmartRootRedirect />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </PrebootDismisser>
    </Suspense>
  );
}

export default AppRoutes;
